import type { WhatsNewRichTextDocument } from '@fordeer/ui-components'
import { Text } from '@shopify/polaris'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import type { ReactElement, ReactNode } from 'react'
import styles from './WhatsNewRichText.module.scss'

type AssetTarget = {
  fields?: {
    file?: {
      url?: string
      contentType?: string
    }
    title?: string
  }
}

const normalizeAssetUrl = (url?: string) => {
  if (!url) return undefined
  return url.startsWith('http') ? url : `https:${url}`
}

const embeddedAsset = (node: { data?: { target?: AssetTarget } }): ReactNode => {
  const file = node?.data?.target?.fields?.file
  const url = normalizeAssetUrl(file?.url)
  if (!url) return null

  const mime = file?.contentType ?? ''
  if (mime.startsWith('video/')) {
    return (
      <video
        key={url}
        controls
        style={{ width: '100%', borderRadius: 12, maxHeight: 420 }}
        src={url}
      >
        <track kind="captions" />
      </video>
    )
  }

  return (
    <img
      key={url}
      alt=""
      src={url}
      style={{ width: '100%', borderRadius: 12, display: 'block' }}
    />
  )
}

const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: { data?: { target?: AssetTarget } }, children: ReactNode) =>
      embeddedAsset(node) ?? children,
    [INLINES.HYPERLINK]: (
      node: { data?: { uri?: string } },
      children: ReactNode,
    ) => (
      <a href={node?.data?.uri} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    [INLINES.ASSET_HYPERLINK]: (
      node: { data?: { target?: AssetTarget } },
      children: ReactNode,
    ) => {
      const url = normalizeAssetUrl(node?.data?.target?.fields?.file?.url)
      if (!url) return children
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      )
    },
  },
}

interface WhatsNewRichTextProps {
  document: WhatsNewRichTextDocument | null | undefined
}

export const WhatsNewRichText = ({ document }: WhatsNewRichTextProps): ReactElement => {
  if (!document?.content?.length) {
    return (
      <Text as="p" variant="bodyMd" tone="subdued">
        No additional details were provided for this update.
      </Text>
    )
  }

  return (
    <div className={styles.richText}>
      {documentToReactComponents(document as never, options)}
    </div>
  )
}
